/* eslint-disable max-len */
/* eslint-disable max-statements, complexity */
import type { MEDIA_PROCESSING_STATUS_V2 } from '~/entities/content/media/composables/media-format'
import { MAP_MEDIA_PROCESSING_STATUS_V2_TO_V1 } from '~/entities/content/media/composables/media-format'
import type { IMedia, IMediaResponse, IMediaResponseV2 } from '~/entities/content/media/interfaces/media'
import type { IMediaType } from '~/entities/content/media/interfaces/media-type'
import { getContainerTypeBySubjectUri } from '~/entities/content/utils/community-type'

import { dateParser, v2ApiMediaTypeParser } from '~/features-shared/content/api/parser-contents'
import { PublicationState } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { timeToMilliseconds } from '~/features-shared/date/utils/time'

import type { SwymResponse } from '~/interfaces/services-dto'

export async function mediasParser(responses: SwymResponse<IMediaResponse[]>): Promise<IMedia[]> {
  return await Promise.all(responses.result.map(media => _mediaResponseParser(media)))
}

export async function mediaParser(response: SwymResponse<IMediaResponse>, isV2?: boolean): Promise<SwymResponse<IMedia>> {
  return { ...response, result: await _mediaResponseParser(response.result, isV2) }
}

// FIXME: Need to use contentParser instead
async function _mediaResponseParser(media: IMediaResponse, isV2?: boolean) {
  // Parser for Media V2 API
  if (isV2)
    media = await mediaV2Parser(media as IMediaResponseV2)

  const parsed_media: IMedia = {
    id: media.id,
    media_id: media.id,
    title: media.title,
    preview_fingerprint: media.preview_fingerprint,
    preview: {
      subject_uri: media.subject6wuri,
      preview_fingerprint: media.preview_fingerprint,
      width: media.width,
      height: media.height,
      processing_version: media.processing_version,
      status: media.status,
    },
    width: media.width,
    height: media.height,
    processing_version: media.processing_version,
    status: media.status,
    preview_media_processing_status: media.preview_media_processing_status || media.status,
    type: media.media_type as IMediaType,
    model: IContentModel.Media,
    subject_uri: media.subject6wuri,
    author: media.author,
    stats_viewed: media.stats_viewed,
    community_title: media?._community?.title,
    community_id: media?._community?.id,
    community_subject_uri: [...media.subject6wuri.split(':')
      .splice(0, 3), 'community', media?._community?.id].join(':'),
    community_type: media?._community?.community_type,
    created_at: dateParser(media.crdate),
    updated_at: dateParser(media.update),
    download_authorization: media.download_original_file === '1',
    download_original_file_url: media.play?.download_original_url.transient_url_as_attachment,
    transient_original_file_url: media.play?.download_original_url.transient_url,
    delete_authorization: media?._acl?.delete.access,
    forward_authorization: media?._acl?.forward.access,
    edit_authorization: media?._acl?.update.access,
    media_file_ext: media?.media_file_ext,
    stream: media.play?.av_streams
      || media.play?.['3d_streams']
      || media.play?.['2d_streams']
      || media.play?.doc_streams,
    view_stream: media.play?.view,
    play: media.play,
    subtitles: media.play?.subtitles,
    chapters: media.play?.chapters,
    original_name: media?.orig_name,
    canBeDisplayedODE: false,
    ratio: media?.ratio,
    size: media?.size,
    related_posts: media?.related_posts,
    wake_up_url: media?.play?.wake_up_url,
    is_illustration: media?.is_illustration,
    _acl: media?._acl,
    reprocessable: media?.reprocessable,
    published: media.published === '1',
    oldParsing: media, // Need it for Story/Sketch/Whiteboard
    description: media?.description,
    enhanced_summary: media?.enhanced_summary,
    thumbnail_time: media?.thumbnail_time ? timeToMilliseconds(media.thumbnail_time) : undefined,
    /* For preview in 3DPlay */
    preview_thumbnails: media?.preview?.thumbnails || [],
  }

  if (parsed_media.community_type === 'externaldm')
    parsed_media.community_type = 'dm'

  // TO REFACTOR: need the backend to always returns original_file_extension
  if (!media.media_file_ext)
    parsed_media.media_file_ext = media.play?.original_file_extension
  // Need it for ODE table document
  if (!parsed_media.media_file_ext && media.orig_name) {
    const tmpExtension = media.orig_name && media.orig_name.split('.')
      .pop()
    parsed_media.media_file_ext = tmpExtension
  }

  // TODO: refactor this when using CRUD file
  if (['docx', 'doc', 'ppt', 'pptx', 'xls', 'xlsx'].includes(parsed_media.media_file_ext))
    parsed_media.canBeDisplayedODE = true

  if (!media.play?.download_original_url.transient_url && parsed_media.stream) {
    parsed_media.stream.forEach((stream) => {
      if (stream.transient_url)
        parsed_media.transient_original_file_url = stream.transient_url
    })
  }

  return parsed_media as unknown as IMedia
}

async function mediaV2Parser(media: IMediaResponseV2) {
  media.preview_fingerprint = media.fingerprint as string
  media.status = MAP_MEDIA_PROCESSING_STATUS_V2_TO_V1[media.processing_state as MEDIA_PROCESSING_STATUS_V2]
  media.media_type = v2ApiMediaTypeParser(media.media_type)
  media.stats_viewed = media.views as string

  media._community = media._community ?? {}
  media._community.title = media.container?.title as string
  media._community.id = media.container?.id as string
  media._community.community_type = await getContainerTypeBySubjectUri(media.container?.subject6wuri)

  media.crdate = media.creation_date as string
  media.update = media.modification_date as string
  media.download_original_file = media.original_file_download ? '1' : '0'

  media._acl = media._acl ?? {}
  media._acl.delete = media._acl.delete ?? {}
  media._acl.forward = media._acl.forward ?? {}
  media._acl.update = media._acl.update ?? {}
  media._acl.delete.access = media.acl?.delete as boolean
  media._acl.forward.access = media.acl?.forward as boolean
  media._acl.update.access = media.acl?.update as boolean

  media.orig_name = media.original_file_name as string
  media.is_illustration = media.is_illustration ? '1' : '0'

  // media._acl = media.acl
  media.published = media.publication_state === PublicationState.Published ? '1' : '0'
  media.description = media.summary as string

  return media
}
